export const INTEGRATION_USER_DEFINED_TAGS_FLAG =
  'integration-user-defined-tags';

export const PLERI_PAGE_LOADER_FLAG = 'pleri-page-loader';

/**
 * Asset ownership: the Ownership page, the owner panel, chips, filters and
 * the owner condition in asset groups. On for everyone; set it to false to
 * hide them again.
 */
export const ASSET_OWNERSHIP_FLAG = 'asset-ownership';
export const AWS_ORGANIZATION_ONBOARDING_FLAG = 'aws-organization-onboarding';

/**
 * The Access tab on an asset's page. The backend evaluates every tenant;
 * this flag decides who sees the tab. On for ACCESS_GRAPH_ORGS in every
 * environment, and off for everyone else until release; turn it on in a
 * browser with localStorage.setItem('plerion.access-graph', 'true').
 */
export const ACCESS_GRAPH_FLAG = 'access-graph';

/**
 * The package filter on the asset inventory. On since package search was made
 * fast on the largest tenant (service-sbom#92); set it to false to hide the
 * filter again.
 */
export const ASSET_PACKAGE_FILTER_FLAG = 'asset-package-filter';

/** What a flag rule may look at: the signed-in user's organization. */
export type FlagContext = { organizationId?: string };

/** Organizations that see the Access tab before release, in every environment. */
export const ACCESS_GRAPH_ORGS: string[] = [
  // The access graph's test organization on develop2.
  '3dc5d5c4-715b-4e90-81cc-d0f30ac38466',
  // The access graph's test organization in production.
  '054396b9-6962-4948-ac2c-d5d7968861dd',
  // Plerion's demo organization (PLERION_DEMO_ORG_ID in search and
  // workload-security).
  'e9a9475a-1390-429c-b621-6fc4aab69ddb',
  // Plerion's own organization.
  '872b3caf-f0ce-4f08-bcc2-bab23596e165',
  // A development organization on develop2 with the Sandbox7 integration,
  // for UI work on the tab.
  'dd884b94-d582-4ed3-ad4d-a89ab7ced6c9',
];

const accessGraphOn = ({ organizationId }: FlagContext) =>
  ACCESS_GRAPH_ORGS.includes(organizationId ?? '');

/**
 * Flags decided in the web app itself: on or off, or a rule worked out in
 * the browser. Flipping one here changes it for everyone with the next
 * web-app deploy; the backend flag list plays no part. The localStorage
 * override still turns one on for a single browser while it is off here.
 */
export const CLIENT_FEATURE_FLAGS: Record<
  string,
  boolean | ((context: FlagContext) => boolean)
> = {
  [INTEGRATION_USER_DEFINED_TAGS_FLAG]: true,
  [PLERI_PAGE_LOADER_FLAG]: true,
  [ASSET_OWNERSHIP_FLAG]: true,
  [ACCESS_GRAPH_FLAG]: accessGraphOn,
  // The one AWS tile. Off until the rest of its work is done; set to true to
  // release it. Until then only the localStorage override turns it on.
  [AWS_ORGANIZATION_ONBOARDING_FLAG]: false,
  [ASSET_PACKAGE_FILTER_FLAG]: true,
};

// Dev override: run this in the browser console to enable a flag locally
// without a deploy:
//   localStorage.setItem('plerion.<flag>', 'true')
export function isDevOverrideEnabled(flag: string): boolean {
  try {
    return localStorage.getItem(`plerion.${flag}`) === 'true';
  } catch {
    return false;
  }
}

/** Whether a client-side flag is on for this browser. */
export function isClientFeatureEnabled(
  flag: string,
  context: FlagContext = {},
): boolean {
  const value = CLIENT_FEATURE_FLAGS[flag];
  const on = typeof value === 'function' ? value(context) : value === true;
  return on || isDevOverrideEnabled(flag);
}
